import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import qs from "qs";
import moment from "moment";
import { CommissionStatus } from "../ContractCommissions/constants";

// Search params live in the URL (source of truth). The form is uncontrolled and
// read on submit; `params` below seed the inputs and survive deep links and
// back/forward navigation. Pushing new params to the URL triggers the effect,
// which runs the fetch.
export function useContractSearch({ api, setContracts, onResults, onError }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    searchTerm: urlSearchTerm,
    commissionStatus: urlCommissionStatus,
    signedStartDate: urlSignedStartDate,
    signedEndDate: urlSignedEndDate,
  } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const params = {
    searchTerm: urlSearchTerm ?? "",
    commissionStatus: urlCommissionStatus ?? CommissionStatus.Pending,
    signedStartDate:
      urlSignedStartDate ?? moment().add(-7, "days").format("YYYY-MM-DD"),
    signedEndDate: urlSignedEndDate ?? moment().format("YYYY-MM-DD"),
  };

  // Memoized so consumers can safely depend on it in effects/callbacks
  // without triggering re-runs on every render.
  const search = useCallback(
    async (term, status, startDate, endDate) => {
      const query = qs.stringify(
        {
          Query: term,
          CommissionStatus: status,
          SignedStartDate: startDate,
          SignedEndDate: endDate,
        },
        { allowDots: true, encode: false, addQueryPrefix: true },
      );
      setIsLoading(true);
      try {
        const response = await api.get("/api/commission/contracts" + query, {
          suppressGlobalError: true,
        });
        if (response.status === 200) {
          setContracts(response.data.results);
          onResults?.();
        }
      } catch (error) {
        onError?.(error);
      } finally {
        setIsLoading(false);
      }
    },
    [api, setContracts, onResults],
  );

  // Push the params to the URL; the effect below reacts and runs the search.
  const searchAndSetHistory = useCallback(
    (term, status, startDate, endDate) => {
      navigate(
        location.pathname +
          qs.stringify(
            {
              searchTerm: term,
              commissionStatus: status,
              signedStartDate: startDate,
              signedEndDate: endDate,
            },
            { addQueryPrefix: true },
          ),
      );
    },
    [navigate, location.pathname],
  );

  // Fetch whenever the URL changes — initial load, submit, and back/forward.
  useEffect(() => {
    search(
      params.searchTerm,
      params.commissionStatus,
      params.signedStartDate,
      params.signedEndDate,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlSearchTerm,
    urlCommissionStatus,
    urlSignedStartDate,
    urlSignedEndDate,
  ]);

  return { isLoading, params, search, searchAndSetHistory };
}
